import { ProgressBarProps } from "./types";

/**
 * ProgressBar (TypeScript + Tailwind CSS)
 * - Determinate when `value` provided (0..max)
 * - Indeterminate when `value` is undefined
 */
export const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  max = 100,
  size = "md",
  colorClass = "bg-blue-600",
  showLabel = true,
  striped = false,
  animated = true,
  className = "",
  ariaLabel = "Progress",
}) => {
  const safeValue =
    value === undefined ? undefined : Math.max(0, Math.min(max, value));
  const percent =
    safeValue === undefined ? 0 : Math.round((safeValue / max) * 100);

  const heightClass = size === "sm" ? "h-2" : size === "lg" ? "h-4" : "h-3";

  const stripeStyle: React.CSSProperties | undefined = striped
    ? {
        backgroundImage:
          "repeating-linear-gradient(45deg, rgba(255,255,255,0.15) 0 10px, rgba(0,0,0,0.05) 10px 20px)",
      }
    : undefined;

  return (
    <div className={`w-full ${className}`.trim()}>
      <style>{`@keyframes indeterminate-translate {
                0% { transform: translateX(-40%); }
                100% { transform: translateX(140%); }
                }
                @keyframes stripe-move {
                0% { background-position: 0 0; }
                100% { background-position: 40px 0; }
                }`}</style>

      <div
        role="progressbar"
        aria-label={ariaLabel}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={safeValue}
        className={`bg-gray-200 dark:bg-gray-700 overflow-hidden ${heightClass} relative`}
      >
        {safeValue !== undefined ? (
          <div
            className={`absolute left-0 top-0 bottom-0 ${colorClass} flex items-center justify-center text-white text-xs select-none`}
            style={{ width: `${percent}%`, transition: "width 300ms ease" }}
          >
            <div
              aria-hidden
              style={{
                ...(striped ? stripeStyle : undefined),
                width: "100%",
                height: "100%",
              }}
              className={`${striped && animated ? "" : ""}`}
            />
          </div>
        ) : (
          <div
            aria-hidden
            className={`absolute left-0 top-0 bottom-0 overflow-hidden`}
          >
            <div
              style={{
                width: "40%",
                height: "100%",
                transform: "translateX(-40%)",
                animation: "indeterminate-translate 1.2s linear infinite",
              }}
              className={`${colorClass} `}
            />
          </div>
        )}
      </div>

      {showLabel && (
        <div className="mt-2 text-sm text-gray-700 dark:text-gray-300 flex items-center justify-between">
          <span className="truncate">{ariaLabel}</span>
          <span className="ml-4">
            {safeValue === undefined ? "…" : `${percent}%`}
          </span>
        </div>
      )}

      {striped && animated && safeValue !== undefined && (
        <style>{`
.progressbar-stripe-animated {
animation: stripe-move 1s linear infinite;
background-image: repeating-linear-gradient(45deg, rgba(255,255,255,0.12) 0 10px, rgba(0,0,0,0.03) 10px 20px);
}
`}</style>
      )}
    </div>
  );
};

export default ProgressBar;
